import type { TranscriptMessageValues } from "@live-writing/models";
import { Loader2, X as XIcon } from "lucide-react";
import { useRef } from "react";

interface TranscriptModalProps {
  open: boolean;
  onClose: () => void;
  student?: { first_name: string; last_name_initial: string } | null;
  transcript: TranscriptMessageValues[];
  loading?: boolean;
  error?: string | null;
}

export default function TranscriptModal({
  open,
  onClose,
  student,
  transcript,
  loading,
  error,
}: TranscriptModalProps) {

  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="transcript-title"
        aria-describedby="transcript-desc"
        className="relative z-50 w-full max-w-3xl bg-white rounded-lg shadow-lg p-6 mx-4"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 id="transcript-title" className="text-lg font-semibold">
              {student
                ? `${student.first_name} ${student.last_name_initial}.`
                : "Transcript"}
            </h3>
            <p id="transcript-desc" className="text-sm text-muted-foreground">
              {student ? "Submitted transcript" : "Transcript"}
            </p>
          </div>
          <div>
            <button
              ref={closeBtnRef}
              onClick={onClose}
              aria-label="Close transcript"
              title="Close"
              className="p-1 rounded hover:bg-gray-100 focus:outline-none"
            >
              <XIcon className="h-6 w-6 text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="mt-3 border rounded p-4 max-h-[60vh] overflow-auto bg-gray-50">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading transcript...
            </div>
          )}

          {error && <div className="text-sm text-red-600">{error}</div>}

          {!loading && !error && transcript.length === 0 && (
            <div className="text-sm text-muted-foreground">
              No transcript available
            </div>
          )}

          {!loading && transcript.length > 0 && (
            <div className="space-y-3">
              {transcript.map((m, i) => {
                const roleLabel = m.role;
                const isUser = m.role === "user";
                const isAssistant = m.role === "assistant";
                const alignment = isUser ? "justify-end" : "justify-start";
                const bubbleBg = isUser
                  ? "bg-blue-50"
                  : isAssistant
                    ? "bg-green-50"
                    : "bg-gray-100";
                const bubbleText = isUser
                  ? "text-blue-800"
                  : isAssistant
                    ? "text-green-800"
                    : "text-gray-700";
                const bubbleRadius = isUser
                  ? "rounded-tl-lg rounded-tr-lg rounded-bl-lg"
                  : "rounded-tr-lg rounded-tl-lg rounded-br-lg";
                return (
                  <div key={i} className={`flex ${alignment}`}>
                    <div
                      className={`max-w-[80%] ${bubbleBg} p-3 ${bubbleRadius} shadow-sm`}
                    >
                      <div
                        className={`text-xs font-semibold mb-1 ${bubbleText}`}
                      >
                        {roleLabel.toUpperCase()}
                      </div>
                      <div className="text-sm whitespace-pre-wrap">
                        {m.content}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
